import React from 'react';
import {
  INVENTORY_COLORS_PRODUCT_GROUP_ID,
  INVENTORY_OPTIONS_PRODUCT_GROUP_ID
} from 'core/constants';
import { SearchedProduct } from 'core/types';
import ListCard from 'components/cards/list/ListCard';
import { getFallbackProductImage } from 'core/util/product-images';
import HomeSelectionListRowDesktop, {
  inventoryItemToHomeSelectionRowProps
} from 'features/shared/inventory/selections/home-selections/components/HomeSelectionListRowDesktop';
import HomeSelectionListRowMobile from 'features/shared/inventory/selections/home-selections/components/HomeSelectionListRowMobile';
import ColorSelectionListRowDesktop, {
  inventoryItemToColorSelectionRowProps
} from 'features/shared/inventory/selections/color-selections/components/ColorSelectionListRowDesktop';
import ColorSelectionListRowMobile from 'features/shared/inventory/selections/color-selections/components/ColorSelectionListRowMobile';

interface SearchedGroupMeta {
  id: number;
  name: string;
}

interface SearchedProductsGroupListViewProps {
  searchedProducts: SearchedProduct[];
  filteredInventory: SearchedGroupMeta[];
  isDesktop: boolean;
  onNavigateToDetail: (groupId: number, productId: number) => void;
  onShowModal: (id: number, groupId: number) => void;
}

const displayNameForGroup = (groupId: number, apiName: string): string => {
  if (groupId === INVENTORY_OPTIONS_PRODUCT_GROUP_ID) return 'Home Selections';
  if (groupId === INVENTORY_COLORS_PRODUCT_GROUP_ID) return 'Color Selections';
  return apiName;
};

const imageForItem = (item: SearchedProduct): string => {
  if (item.product_template.image) return item.product_template.image;
  if (item.product_template.manufacturer_image)
    return item.product_template.manufacturer_image;
  return getFallbackProductImage(item.product_template.type);
};

const SearchedProductsGroupListView: React.FC<
  SearchedProductsGroupListViewProps
> = ({
  searchedProducts,
  filteredInventory,
  isDesktop,
  onNavigateToDetail,
  onShowModal
}) => {
  // Options (group 67) and colors (group 68) search results reuse the same
  // display-only accordion row components as their grouped list pages —
  // title/Notes only, no click-through — instead of the generic ListCard,
  // so a search result looks identical to the row it corresponds to on
  // /inventory/group/67 or /inventory/group/68. Every other group still
  // uses ListCard with its pill/model fields and click-through.
  const renderCard = (product: SearchedProduct) => {
    const { id, name, group_id, product_template } = product;

    if (group_id === INVENTORY_OPTIONS_PRODUCT_GROUP_ID) {
      const rowProps = inventoryItemToHomeSelectionRowProps(product);
      return isDesktop ? (
        <HomeSelectionListRowDesktop
          key={`searched-product-${id}`}
          {...rowProps}
        />
      ) : (
        <HomeSelectionListRowMobile
          key={`searched-product-${id}`}
          {...rowProps}
        />
      );
    }

    if (group_id === INVENTORY_COLORS_PRODUCT_GROUP_ID) {
      const rowProps = inventoryItemToColorSelectionRowProps(product);
      return isDesktop ? (
        <ColorSelectionListRowDesktop
          key={`searched-product-${id}`}
          {...rowProps}
        />
      ) : (
        <ColorSelectionListRowMobile
          key={`searched-product-${id}`}
          {...rowProps}
        />
      );
    }

    const image = imageForItem(product);
    const css = image?.indexOf('/static/media') === -1 ? '' : 'no-image';
    return (
      <ListCard
        className={css}
        key={`searched-product-${id}`}
        title={!!name ? name : product_template.name}
        descriptionOne={'Model #' + product_template.model}
        image={image}
        onClick={() => {
          isDesktop
            ? onNavigateToDetail(group_id, id)
            : onShowModal(id, group_id);
        }}
      />
    );
  };

  if (!searchedProducts.length) {
    return (
      <div className="hk-inv-search-empty">
        <h1>No Matches Found</h1>
        <p>
          The thing you are searching for isn't here. Please try another search.
        </p>
        <p>You can also add an item {isDesktop ? 'above' : 'below'}.</p>
      </div>
    );
  }

  return (
    <>
      {filteredInventory.map((group) => (
        <div
          className={
            isDesktop
              ? 'hk-inv-desktop-searched-products'
              : 'hk-inv-searched-products'
          }
          key={`searched-group-${group.id}`}
        >
          <h4>{displayNameForGroup(group.id, group.name)}</h4>
          {searchedProducts
            .filter((p) => p.group_id === group.id)
            .map((p) => renderCard(p))}
        </div>
      ))}
    </>
  );
};

export default SearchedProductsGroupListView;
